import React, { useState, useEffect ,useCallback} from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBookings, cancelBooking } from '../services/api';

//import { useMerchant } from '../contexts/MerchantContext';
import { format } from 'date-fns';

function BookingList() {
  const { merchantSlug } = useParams();
  //const { merchant } = useMerchant();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await getBookings({}, merchantSlug);
      // Handle paginated response - extract results array
      setBookings(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [merchantSlug]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleCancel = async (bookingId) => {
    if (window.confirm('確定要取消這個體驗嗎？')) {
      try {
        await cancelBooking(bookingId, merchantSlug);
        fetchBookings();
        alert('體驗已取消');
      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert('取消體驗失敗');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: '待確認', color: 'bg-warning/20 text-warning' },
      'confirmed': { text: '已確認', color: 'bg-primary/20 text-primary' },
      'completed': { text: '已完成', color: 'bg-success/20 text-success' },
      'cancelled': { text: '已取消', color: 'bg-danger/20 text-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-200 text-gray-600' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-primary text-lg">載入中...</div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between whitespace-nowrap px-4 py-3">
        <Link to={`/${merchantSlug}`} className="flex items-center gap-2 text-text-primary">
          <span className="material-symbols-outlined">arrow_back</span>
          <span className="text-lg font-medium">返回</span>
        </Link>
        <h1 className="text-2xl font-bold tracking-tighter text-text-light dark:text-text-dark">我的體驗</h1>
      </header>

      {bookings.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 px-4">
          <div className="text-center">
            <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">event_busy</span>
            <p className="text-text-primary text-lg font-medium mb-2">目前沒有體驗記錄</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">立即體驗您需要的服務</p>
            <Link
              to={`/${merchantSlug}`}
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white text-base font-bold rounded-lg hover:bg-primary/90 transition-colors"
            >
              開始體驗
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          {bookings.map(booking => (
            <div key={booking.id} className="bg-white dark:bg-background-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-primary flex items-center justify-center rounded-full bg-primary/10 shrink-0 size-12">
                    <span className="material-symbols-outlined">calendar_month</span>
                  </div>
                  <div>
                    <h3 className="text-text-light dark:text-text-dark text-lg font-semibold">
                      {booking.service_name}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      體驗編號: #{booking.id}
                    </p>
                  </div>
                </div>
                {getStatusBadge(booking.status)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-500 text-sm">person</span>
                    <span className="text-text-light dark:text-text-dark text-sm">
                      {booking.customer_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-500 text-sm">phone</span>
                    <span className="text-text-light dark:text-text-dark text-sm">
                      {booking.customer_phone}
                    </span>
                  </div>
                  {booking.customer_email && (
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-gray-500 text-sm">email</span>
                      <span className="text-text-light dark:text-text-dark text-sm">
                        {booking.customer_email}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-500 text-sm">calendar_today</span>
                    <span className="text-text-light dark:text-text-dark text-sm">
                      {booking.booking_date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-500 text-sm">schedule</span>
                    <span className="text-text-light dark:text-text-dark text-sm">
                      {booking.booking_time}
                    </span>
                  </div>
                  {booking.total_amount && (
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-gray-500 text-sm">payments</span>
                      <span className="text-text-light dark:text-text-dark text-sm font-semibold">
                        NT$ {booking.total_amount}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {booking.notes && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    <span className="font-medium">備註：</span>{booking.notes}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  建立時間：{format(new Date(booking.created_at), 'yyyy-MM-dd HH:mm')}
                </p>
                
                <div className="flex gap-2">
                  <Link
                    to={`/confirmation/${booking.id}`}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-text-light dark:text-text-dark text-sm font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    查看詳情
                  </Link>
                  
                  {booking.status === 'pending' && (
                    <button
                      onClick={() => handleCancel(booking.id)}
                      className="inline-flex items-center px-3 py-1.5 bg-danger/10 text-danger text-sm font-medium rounded-lg hover:bg-danger/20 transition-colors"
                    >
                      取消體驗
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 left-0 right-0 p-4 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700">
        <Link
          to={`/${merchantSlug}`}
          className="w-full flex items-center justify-center rounded-lg h-12 bg-primary text-white text-base font-bold leading-normal tracking-wide hover:bg-primary/90 transition-colors"
        >
          體驗新服務
        </Link>
      </div>
    </>
  );
}

export default BookingList;